import { AddCircleRounded, Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  IconButton,
  Link,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  DataGrid,
  GridCellModes,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { MuiFileInput } from "mui-file-input";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi, { httpClient } from "../../hooks/useApi";
import Constants from "../../utils/Constants";
import FormDialog from "../FormDialog";
import renderCellExpand from "./DataGridCellExpand";

const rowHeight = "30px";
const nonEditableCellClass = "cell-non-editable";

const ReceivableAndPayableOptions = [
  { label: "債権", value: "RECEIVABLE" },
  { label: "債務", value: "PAYABLE" },
];
const DifferencesExistenceOptions = [
  { label: "有り", value: true },
  { label: "無し", value: false },
];

// TODO:IME合成時のハンドリング
export default function ReceivablePayableTable({
  confirmationLetterId,
  companyId,
  value = [],
  onChange = () => {},
  saveHandler = () => {},
  postSaveHandler = () => {},
  readOnly = false,
}) {
  const apiRef = useGridApiRef();
  const role = useSelector((state) => state.role.role);
  const { connect } = useApi();

  const [rows, setRows] = useState([]);
  useEffect(() => {
    setRows(
      (value ?? []).map((row) => ({
        ...row,
        __rowId: row.__rowId ?? row.id ?? crypto.randomUUID(),
      }))
    );
  }, [value]);

  const [accountTitles, setAccountTitles] = useState([]);

  const fetchAccountTitles = useCallback(async () => {
    const accountTitleFetchResult = await httpClient.get(
      `/api/auditor/GetAccountTitles?companyId=${companyId}`
    );
    setAccountTitles(accountTitleFetchResult?.data?.data);
  }, [companyId]);

  useEffect(() => {
    fetchAccountTitles();
  }, [fetchAccountTitles]);

  // 勘定科目オートコンプリートのコンポーネント
  function EditAccountCell(props) {
    const {
      id, //行ID
      field, //カラム名
      value, //値
    } = props;
    const apiRef = useGridApiContext();

    // 行データ取得
    const row = apiRef.current.getRow(id);

    // 債権・債務ごとに選択肢を切り替え
    const filteredOptions = accountTitles
      .filter((opt) => opt.accountTitleType === row.kubun)
      .map((opt) => opt.accountTitle);

    // 変更時にDataGridへ値を反映する
    const handleChange = (event, newValue) => {
      apiRef.current.setEditCellValue({ id, field, value: newValue }, event);
    };

    return (
      <Autocomplete
        freeSolo
        options={filteredOptions}
        value={value || ""}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField {...params} variant="standard" sx={{ height: "100%" }} />
        )}
        size="small"
        fullWidth
        sx={{
          "& .MuiInputBase-root": { height: "100%" },
        }}
      />
    );
  }
  // 単位オートコンプリートのコンポーネント
  function EditUnitCell(props) {
    const {
      id, //行ID
      field, //カラム名
      value, //値
    } = props;
    const apiRef = useGridApiContext();

    // 変更時にDataGridへ値を反映する
    const handleChange = (event, newValue) => {
      apiRef.current.setEditCellValue({ id, field, value: newValue }, event);
    };

    return (
      <Autocomplete
        freeSolo
        options={["¥", "$", "€"]}
        value={value || ""}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField {...params} variant="standard" sx={{ height: "100%" }} />
        )}
        size="small"
        fullWidth
        clearIcon={false}
        sx={{
          "& .MuiInputBase-root": { height: "100%" },
        }}
      />
    );
  }
  // 上部ツールバーコンポーネント
  function HeaderToolbar({ setRows, onChange, rows, selectionModel }) {
    const [showDelete, setShowDelete] = useState(false);

    useEffect(() => {
      // データが変更された時のみ削除ボタンを表示
      setShowDelete(selectionModel?.length > 0);
    }, [selectionModel?.length]);

    const handleAddClick = () => {
      const rid = crypto.randomUUID();
      const newRows = [
        ...rows,
        {
          __rowId: rid,
          id: "",
          confirmationLetterId,
          kubun: "",
          type: "",
          details1: "",
          volume: undefined,
          unit: "",
          details2: "",
        },
      ];
      setRows(newRows);
      onChange(newRows);
    };

    const handleDeleteClick = () => {
      if (selectionModel?.length === 0) return;
      const newRows = rows.filter(
        (row) => !selectionModel.includes(row.__rowId)
      );
      setRows(newRows);
      onChange(newRows);
    };

    return (
      <Toolbar>
        <Tooltip title="Add">
          <IconButton onClick={handleAddClick}>
            <AddCircleRounded />
            <Typography variant="body2">追加</Typography>
          </IconButton>
        </Tooltip>
        {showDelete && (
          <Tooltip title="Delete">
            <IconButton
              onClick={handleDeleteClick}
              color="error"
              sx={{ ml: 1 }}
            >
              <Delete />
              <Typography variant="body2">削除</Typography>
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
    );
  }

  // --------------------ファイル追加関連
  const [targetId, setTargetId] = useState(null);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const handleFileDialogClose = () => {
    setTargetId(null);
    setFileDialogOpen(false);
    setAttachment(null);
  };
  const handleClickAddFile = (event, rowId) => {
    event.stopPropagation();
    setTargetId(rowId);
    setFileDialogOpen(true);
  };
  const handleAttachment = async () => {
    await saveHandler();
    const queryParams = new URLSearchParams({
      companyId: companyId,
      confirmationLetterId: confirmationLetterId,
      confirmationDetailId: targetId,
      filename: attachment.name,
      contentType: attachment.type,
    }).toString();
    const result = await connect(() => {
      return httpClient.post(
        `/api/${role}/AttachToConfirmationDetail?${queryParams}`,
        attachment,
        {
          headers: {
            "Content-Type": "application/octet-stream",
          },
        }
      );
    }, "アップロードしました");
    if (result?.status === 200) {
      handleFileDialogClose();
    }
    postSaveHandler();
  };

  /**
   * 添付ファイル削除
   * @param {*} id
   */
  const handleFileDelete = async (id) => {
    await saveHandler();
    await connect(() => {
      return httpClient.post(
        `/api/auditor/DeleteConfirmationAttachment?id=${id}`
      );
    }, "削除しました");
    postSaveHandler();
  };

  /**
   * 添付ファイルセルの共通レンダラー
   * @param {object} params - DataGridのrenderCellパラメータ
   * @param {string[]} targetTypes - 表示対象のattachmentType（大文字）
   */
  function renderAttachmentCell(params, targetTypes) {
    const { row } = params;
    const attachments = row?.attachments;
    return (
      <>
        {attachments
          ?.filter((attachment) =>
            targetTypes.includes(attachment.attachmentType)
          )
          .map((attachment) => (
            <Box
              key={attachment.id}
              sx={{
                display: "flex",
                alignItems: "center",
                height: rowHeight,
                minWidth: 0,
              }}
            >
              {/* ダウンロードリンク */}
              <Link
                underline="always"
                href={`/api/auditor/GetAttachmentOfConfirmation?id=${attachment.id}`}
                download
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: rowHeight,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {attachment.filename}
              </Link>
              {/* 削除ボタン */}
              {!readOnly && targetTypes.includes(role.toUpperCase()) && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleFileDelete(attachment.id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
      </>
    );
  }

  // ファイル追加ボタンの共通コンポーネント
  function AttachmentAddButton({ id, disabled, tooltip }) {
    return (
      <Tooltip title={tooltip || ""}>
        <span>
          <Button
            onClick={(event) => handleClickAddFile(event, id)}
            size="small"
            variant="outlined"
            sx={{ margin: "0.1rem", fontSize: "0.7rem" }}
            disabled={disabled}
          >
            ファイル追加
          </Button>
        </span>
      </Tooltip>
    );
  }

  // ヘッダー定義
  const columns = [
    {
      field: "kubun",
      headerName: "区分",
      editable: role !== Constants.ROLE.CONFIRMER && !readOnly,
      width: 80,
      type: "singleSelect",
      valueOptions: ReceivableAndPayableOptions,
      renderCell: ({ value }) => {
        const option = ReceivableAndPayableOptions.find(
          (opt) => opt.value === value
        );
        return (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>{option?.label}</div>
            <ArrowDropDownIcon />
          </Box>
        );
      },
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "type",
      headerName: "勘定科目",
      editable: role !== Constants.ROLE.CONFIRMER && !readOnly,
      width: 120,
      renderCell: renderCellExpand,
      renderEditCell: (params) => <EditAccountCell {...params} />,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "details1",
      headerName: "細目",
      editable: role !== Constants.ROLE.CONFIRMER && !readOnly,
      width: 120,
      renderCell: renderCellExpand,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "unit",
      headerName: "単位",
      editable: role !== Constants.ROLE.CONFIRMER && !readOnly,
      width: 50,
      renderCell: renderCellExpand,
      renderEditCell: (params) => <EditUnitCell {...params} />,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "volume",
      headerName: "金額",
      type: "number",
      editable: role !== Constants.ROLE.CONFIRMER && !readOnly,
      width: 100,
      renderCell: renderCellExpand,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "details2",
      headerName: "備考",
      editable: role !== Constants.ROLE.CONFIRMER && !readOnly,
      width: 120,
      renderCell: renderCellExpand,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "attachmentClient",
      headerName: "添付ファイル(被監査企業)",
      editable: false,
      renderCell: (params) =>
        renderAttachmentCell(params, [
          Constants.ROLE.AUDITOR.toUpperCase(),
          Constants.ROLE.CLIENT.toUpperCase(),
        ]),
      cellClassName:
        readOnly || role === Constants.ROLE.CONFIRMER
          ? nonEditableCellClass
          : "",
      width: 120,
    },
    {
      field: "clientAttachmentButton",
      headerName: "",
      renderCell: ({ row }) => (
        <AttachmentAddButton
          id={row?.id}
          disabled={!row?.id}
          tooltip={
            row?.id
              ? ""
              : "ファイルを追加するには先に保存ボタンを押してください"
          }
        />
      ),
      width: 120,
    },
    {
      field: "differencesExistence",
      headerName: "相違の有無",
      type: "singleSelect",
      valueOptions: DifferencesExistenceOptions,
      editable: role === Constants.ROLE.CONFIRMER && !readOnly,
      renderCell: ({ value }) => {
        const option = DifferencesExistenceOptions.find(
          (opt) => opt.value === value
        );
        return (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>{option?.label}</div>
            <ArrowDropDownIcon />
          </Box>
        );
      },
      width: 120,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "differences1",
      headerName: "相違内容",
      editable: role === Constants.ROLE.CONFIRMER && !readOnly,
      width: 120,
      renderCell: renderCellExpand,
      cellClassName: (params) =>
        !params.colDef.editable ? nonEditableCellClass : "",
    },
    {
      field: "attachmentConfirmer",
      headerName: "添付ファイル(確認先)",
      editable: false,
      renderCell: (params) =>
        renderAttachmentCell(params, [Constants.ROLE.CONFIRMER.toUpperCase()]),
      cellClassName:
        role === Constants.ROLE.CONFIRMER && !readOnly
          ? ""
          : nonEditableCellClass,
      width: 120,
    },
    {
      field: "confirmerAttachmentButton",
      headerName: "",
      renderCell: ({ row }) => (
        <AttachmentAddButton id={row?.id} disabled={false} />
      ),
      width: 120,
    },
  ];

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓https://mui.com/x/react-data-grid/recipes-editing/#single-click-editing
  // セルをクリックした場合、編集モードにする
  const handleCellClick = useCallback(
    (params, event) => {
      // セルが編集可能でなければ変種モードにしない
      if (!params.isEditable) {
        return;
      }

      // ポータル（ドロップダウン等の浮動要素）内クリックを無視
      if (
        event.target.nodeType === 1 &&
        !event.currentTarget.contains(event.target)
      ) {
        return;
      }

      // ダブルクリックなど既定のモード切替を抑制
      event.defaultMuiPrevented = true;

      // ワンクリックで編集モードに入れる
      const cellMode = apiRef.current.getCellMode(params.id, params.field);
      // printable key (= 1文字) かつ編集可能セル かつ未編集モード
      if (cellMode === GridCellModes.View && params.isEditable) {
        // DataGrid 側の既定処理の抑制
        event.defaultMuiPrevented = true;
        // 即座に編集モードを開始すると同時に input にフォーカスが移り、
        // このキーイベントもエディタに届く
        apiRef.current.startCellEditMode({
          id: params.id,
          field: params.field,
        });
      }
    },
    [apiRef]
  );
  // ↑↑↑↑↑↑↑↑↑↑↑↑↑↑https://mui.com/x/react-data-grid/recipes-editing/#single-click-editing

  // セル内でキーを押したときのハンドラ
  const handleCellKeyDown = useCallback(
    (params, event) => {
      // Tab押下時右のセルに移動する。Shift+Tab押下時は左のセルへ移動する
      if (event.key === "Tab") {
        // 既定の Tab 動作をキャンセル
        event.preventDefault();
        event.defaultMuiPrevented = true;

        // 現在表示中の列一覧
        const visibleCols = apiRef.current.getVisibleColumns();
        const colIndex = visibleCols.findIndex(
          (col) => col.field === params.field
        );
        const nextColIndex = colIndex + (event.shiftKey ? -1 : 1);

        // Tab (順方向) の場合
        if (!event.shiftKey) {
          if (nextColIndex < visibleCols.length) {
            // 同じ行の隣セルへ移動
            apiRef.current.setCellFocus(
              params.id,
              visibleCols[nextColIndex].field
            );
          } else {
            // 最後の列なら次の行の先頭列へ
            const rowIds = apiRef.current.getSortedRowIds(); // ソート・フィルタ後の行順を取得
            const rowIndex = rowIds.findIndex((id) => id === params.id);
            const nextRowIndex = rowIndex + 1;
            if (nextRowIndex < rowIds.length) {
              const nextId = rowIds[nextRowIndex];
              apiRef.current.setCellFocus(nextId, visibleCols[0].field);
            }
          }
        } else {
          // Shift+Tab (逆方向) の場合
          if (nextColIndex >= 0) {
            apiRef.current.setCellFocus(
              params.id,
              visibleCols[nextColIndex].field
            );
          } else {
            const rowIds = apiRef.current.getSortedRowIds();
            const rowIndex = rowIds.findIndex((id) => id === params.id);
            const prevRowIndex = rowIndex - 1;
            if (prevRowIndex >= 0) {
              const prevId = rowIds[prevRowIndex];
              apiRef.current.setCellFocus(
                prevId,
                visibleCols[visibleCols.length - 1].field
              );
            }
          }
        }
      }

      // —————————————————————————————
      // キー押下で編集モードに入れる
      const cellMode = apiRef.current.getCellMode(params.id, params.field);
      // printable key (= 1文字) かつ編集可能セル かつ未編集モード
      if (
        cellMode === GridCellModes.View &&
        params.isEditable &&
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        // DataGrid 側の既定処理の抑制
        event.defaultMuiPrevented = true;
        // 即座に編集モードを開始すると同時に input にフォーカスが移り、
        // このキーイベントもエディタに届く
        apiRef.current.startCellEditMode({
          id: params.id,
          field: params.field,
        });
      }
    },
    [apiRef]
  );

  // 編集後にvalueをstateとcontrollerに反映
  const processRowUpdate = useCallback(
    (updatedRow, originalRow) => {
      const newRows = rows.map((row) =>
        row.__rowId === updatedRow.__rowId ? updatedRow : row
      );
      setRows(newRows);
      onChange(newRows);
      return updatedRow;
    },
    [rows, onChange]
  );

  // 選択行の管理
  const [selectionModel, setSelectionModel] = useState([]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <DataGrid
          // cellModeModelは使用しないこと（startCellEditMode関数が上手く動かなくなるため）
          rows={rows}
          columns={columns}
          columnVisibilityModel={{
            clientAttachmentButton:
              role !== Constants.ROLE.CONFIRMER && !readOnly,
            confirmerAttachmentButton:
              role === Constants.ROLE.CONFIRMER && !readOnly,
          }}
          apiRef={apiRef}
          editMode="cell"
          onCellKeyDown={handleCellKeyDown}
          onCellClick={handleCellClick}
          processRowUpdate={processRowUpdate}
          getRowId={(row) => row.__rowId}
          slots={{
            toolbar:
              readOnly || role === Constants.ROLE.CONFIRMER
                ? null
                : HeaderToolbar,
          }}
          slotProps={{
            toolbar: { setRows, onChange, rows, selectionModel },
          }}
          checkboxSelection={!readOnly && role !== Constants.ROLE.CONFIRMER}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) =>
            setSelectionModel(newSelection)
          }
          getRowHeight={(params) => {
            // 添付ファイルの数に応じて高さを調整
            const attachments = params.model.attachments || [];
            // 1行あたり30px、高さの最小値は40px
            const baseHeight = 30;
            const perAttachment = 30;
            const clientAttachmentCount = attachments.filter(
              (attachment) =>
                attachment.attachmentType ===
                  Constants.ROLE.AUDITOR.toUpperCase() ||
                attachment.attachmentType ===
                  Constants.ROLE.CLIENT.toUpperCase()
            ).length;
            const confirmerAttachmentCount = attachments.filter(
              (attachment) =>
                attachment.attachmentType ===
                Constants.ROLE.CONFIRMER.toUpperCase()
            ).length;
            const count = Math.max(
              clientAttachmentCount,
              confirmerAttachmentCount
            );
            return Math.max(
              baseHeight,
              baseHeight + (count - 1) * perAttachment
            );
          }}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
        />
      </Box>
      {/* 添付ファイルダイアログ */}
      <FormDialog
        open={fileDialogOpen}
        onClose={handleFileDialogClose}
        title="添付ファイルの追加"
      >
        <MuiFileInput
          value={attachment}
          onChange={(file) => setAttachment(file)}
          placeholder="ファイルを選択してください"
        />
        <Button onClick={handleAttachment} disabled={!attachment}>
          追加
        </Button>
      </FormDialog>
    </>
  );
}
